package fr.frux.shared.data.repository

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.network.api.NotificationsApi
import fr.frux.core.network.error.mapFailureToAppError
import fr.frux.shared.data.mapper.toDomain
import fr.frux.shared.domain.model.NotificationPage
import fr.frux.shared.domain.model.NotificationPreferences
import fr.frux.shared.domain.repository.NotificationRepository
import fr.frux.shared.dto.PushPlatformDto
import fr.frux.shared.dto.UpdateNotificationPreferencesRequest

/**
 * Implementation of NotificationRepository, à l'identique de HarvestRepositoryImpl.
 */
@ContributesBinding(AppScope::class)
@SingleIn(AppScope::class)
@Inject
class NotificationRepositoryImpl(
    private val notificationsApi: NotificationsApi,
) : NotificationRepository {
    companion object {
        private const val TAG = "NotificationRepository"
    }

    override suspend fun getNotifications(
        beforeId: Long?,
        limit: Int,
    ): Result<NotificationPage> =
        runCatchingCancellable { notificationsApi.getNotifications(beforeId, limit).toDomain() }
            .onFailure { Logger.e(TAG, "❌ Chargement des notifications échoué", it) }
            .mapFailureToAppError()

    override suspend fun markRead(ids: List<Long>): Result<Unit> =
        runCatchingCancellable { notificationsApi.markRead(ids) }
            .onFailure { Logger.e(TAG, "❌ Marquage lu des notifications $ids échoué", it) }
            .mapFailureToAppError()

    override suspend fun markAllRead(): Result<Unit> =
        runCatchingCancellable { notificationsApi.markAllRead() }
            .onFailure { Logger.e(TAG, "❌ Marquage lu de toutes les notifications échoué", it) }
            .mapFailureToAppError()

    override suspend fun getPreferences(): Result<NotificationPreferences> =
        runCatchingCancellable { notificationsApi.getPreferences().toDomain() }
            .onFailure { Logger.e(TAG, "❌ Chargement des préférences de notification échoué", it) }
            .mapFailureToAppError()

    override suspend fun updatePreferences(
        emailEnabled: Boolean,
        pushEnabled: Boolean,
        harvestEvents: Boolean,
        treeEvents: Boolean,
        maturityAlerts: Boolean,
    ): Result<NotificationPreferences> =
        runCatchingCancellable {
            notificationsApi
                .updatePreferences(
                    UpdateNotificationPreferencesRequest(
                        emailEnabled = emailEnabled,
                        pushEnabled = pushEnabled,
                        harvestEvents = harvestEvents,
                        treeEvents = treeEvents,
                        maturityAlerts = maturityAlerts,
                    ),
                ).toDomain()
        }.onFailure { Logger.e(TAG, "❌ Mise à jour des préférences de notification échouée", it) }
            .mapFailureToAppError()

    override suspend fun registerPushToken(token: String): Result<Unit> =
        runCatchingCancellable {
            // Android est le seul émetteur au jalon M2 (ADR 0003). Le jour où iOS/web appellent,
            // la plateforme remontera en paramètre — pas avant (CLAUDE.md règle 5).
            notificationsApi.registerPushToken(token, PushPlatformDto.ANDROID)
        }.onFailure { Logger.e(TAG, "❌ Enregistrement du jeton push échoué", it) }
            .mapFailureToAppError()

    override suspend fun revokePushToken(token: String): Result<Unit> =
        runCatchingCancellable { notificationsApi.revokePushToken(token) }
            .onFailure { Logger.e(TAG, "❌ Révocation du jeton push échouée", it) }
            .mapFailureToAppError()
}
